export const EMPTY_EMP = {
  matricola: "", email: "", name: "", password: "", role: "dipendente", gender: "",
  department_id: "", job_title: "Dipendente", ferie_balance: 26, rol_balance: 32, is_external: false, contract_end: "", company: "teleborsa", sede: "Roma",
  sede_patrono_mese: 6, sede_patrono_giorno: 29, sede_patrono_nome: "SS. Pietro e Paolo",
  hire_date: "", ccnl_id: "", level: "", part_time_pct: 100, exfestivita_balance: 0, studio_balance: 150, custom_benefit_ids: [],
  country: "IT", weekly_hours_contract: "", annual_gross: "",
  prev_ferie_balance: 0, prev_rol_balance: 0, prev_exfestivita_balance: 0,
};

export const EMPTY_DEPT = { name: "", description: "", manager_id: "", company: "teleborsa" };

export const EMPTY_CCNL = {
  name: "", sector: "", annual_ferie_days: 26, annual_rol_hours: 32, exfestivita_hours: 32,
  weekly_hours: 40, monthly_salaries: 14, probation_days: 0, notice_days: 0, scatto_months: 24, scatti_max: 0, levels: "", level_terms: [],
  ot_weekday_pct: 15, ot_saturday_pct: 15, ot_holiday_pct: 30, ot_night_pct: 50, notes: "",
};

export const SENIORITY_BRACKETS = ["Fino a 5 anni", "Da 5 a 10 anni", "Oltre 10 anni"];

export const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; } };

// Anzianità in mesi dalla data di assunzione a oggi.
// Un mese matura dopo il 15° giorno di permanenza (giorno 16+ del mese parziale).
export const seniorityMonths = (hireIso, ref = new Date()) => {
  if (!hireIso) return null;
  const h = new Date(hireIso);
  if (Number.isNaN(h.getTime())) return null;
  if (h > ref) return 0;
  let months = (ref.getFullYear() - h.getFullYear()) * 12 + (ref.getMonth() - h.getMonth());
  let dayDiff = ref.getDate() - h.getDate();
  if (dayDiff < 0) {
    months -= 1;
    const prevMonthDays = new Date(ref.getFullYear(), ref.getMonth(), 0).getDate();
    dayDiff = prevMonthDays - h.getDate() + ref.getDate();
  }
  if (dayDiff > 15) months += 1;
  return Math.max(0, months);
};

export const seniorityShort = (hireIso) => {
  const m = seniorityMonths(hireIso);
  if (m === null) return "—";
  const y = Math.floor(m / 12);
  const rem = m % 12;
  if (y === 0) return `${rem} ${rem === 1 ? "mese" : "mesi"}`;
  return rem === 0 ? `${y}a` : `${y}a ${rem}m`;
};

export const seniorityLabel = (hireIso) => {
  const m = seniorityMonths(hireIso);
  if (m === null) return "—";
  const y = Math.floor(m / 12);
  const rem = m % 12;
  const parts = [];
  if (y > 0) parts.push(`${y} ${y === 1 ? "anno" : "anni"}`);
  if (rem > 0 || y === 0) parts.push(`${rem} ${rem === 1 ? "mese" : "mesi"}`);
  return `${m} ${m === 1 ? "mese" : "mesi"}${y > 0 ? ` (${parts.join(" e ")})` : ""}`;
};

export const daysToEnd = (iso) => {
  if (!iso) return null;
  const end = new Date(iso); end.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = Math.round((end - today) / 86400000);
  return Number.isNaN(d) ? null : d;
};

// Scatti di anzianità dal CCNL. Ritorna null se non previsti dal contratto.
export const scattoInfo = (hireIso, ccnl) => {
  if (!hireIso || !ccnl) return null;
  const step = Number(ccnl.scatto_months || 0);
  const max = Number(ccnl.scatti_max || 0);
  if (step <= 0 || max <= 0) return null;
  const months = seniorityMonths(hireIso);
  if (months === null) return null;
  const matured = Math.min(Math.floor(months / step), max);
  const done = matured >= max;
  const nextAt = done ? null : (matured + 1) * step;
  const monthsToNext = done ? null : nextAt - months;
  const reachedNow = matured > 0 && months === matured * step; // scatto maturato questo mese
  const upcoming = !done && monthsToNext !== null && monthsToNext <= 1; // entro un mese
  return { months, matured, max, done, nextAt, monthsToNext, reachedNow, upcoming };
};

export const contractBadge = (iso) => {
  const d = daysToEnd(iso);
  if (d === null) return null;
  if (d < 0) return { cls: "bg-red-100 text-red-700", label: "Scaduto", danger: true };
  if (d <= 30) return { cls: "bg-amber-100 text-amber-700", label: d === 0 ? "Scade oggi" : `Scade tra ${d}gg`, danger: false };
  return null;
};

// Fascia di anzianità di servizio (dalla data di assunzione alla cessazione o a oggi).
export const seniorityBracket = (hireIso, endIso) => {
  const ref = endIso && !Number.isNaN(new Date(endIso).getTime()) ? new Date(endIso) : new Date();
  const m = seniorityMonths(hireIso, ref);
  if (m === null) return null;
  if (m <= 60) return "Fino a 5 anni";
  if (m <= 120) return "Da 5 a 10 anni";
  return "Oltre 10 anni";
};

// Termini contrattuali risolti (prova + preavviso) per il dipendente in base a livello e anzianità.
export const contractTerms = (emp, ccnl) => {
  if (!ccnl) return null;
  const level = emp?.level;
  const term = (ccnl.level_terms || []).find((t) => t.level === level);
  const bracket = seniorityBracket(emp?.hire_date, emp?.contract_end);
  let notice = null;
  let probation = ccnl.probation_days;
  if (term) {
    probation = term.probation_days;
    notice = bracket === "Fino a 5 anni" ? term.notice_up_to_5y
      : bracket === "Da 5 a 10 anni" ? term.notice_5_to_10y
        : bracket === "Oltre 10 anni" ? term.notice_over_10y : null;
  } else {
    notice = ccnl.notice_days;
  }
  return { level, bracket, probation_days: probation, notice_days: notice, hasLevelTerm: !!term };
};
